import { CdkDrag, CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { dayjs } from '@karve.it/core';
import { Store } from '@ngrx/store';

import { SearchResult } from 'minisearch';
import { MenuItem, MessageService, PrimeIcons } from 'primeng/api';
import { ContextMenu, ContextMenuModule } from 'primeng/contextmenu';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { SelectButtonModule } from 'primeng/selectbutton';


import { debounceTime, firstValueFrom, Observable, startWith } from 'rxjs';

import { SubSink } from 'subsink';

import { v4 as uuid } from 'uuid';

import { FreyaCommonModule } from '../../../../freya-common/freya-common.module';
import { EventAttendeeRoles } from '../../../../global.constants';
import { BrandingService } from '../../../../services/branding.service';
import { PermissionService } from '../../../../services/permission.service';
import { TimezoneHelperService } from '../../../../services/timezone-helper.service';
import { SharedModule } from '../../../../shared/shared.module';
import { DispatchAssetSearchService, PotentialAssetType } from '../../dispatch-asset-search.service';
import { generateCrewCsv } from '../../dispatch-csv.helper';
import { DispatchActions } from '../../store/dispatch.actions';
import { AdditionalEventAttendeeRoles, CrewSortOptions, DispatchFilterOption, DispatchUserStatusOptions, PotentialCrewMember, UpdateCrewActionPayload } from '../../store/dispatch.reducer';
import { selectAssetsLoaded, selectDispatchDate, selectPotentialAssets, selectPotentialCrew, selectTodaysRevenue, selectUsersLoaded } from '../../store/dispatch.selectors';

import { AttendeeDragData } from '../dispatch-card/dispatch-card.component';
import { DispatchChipComponent } from '../dispatch-chip/dispatch-chip.component';
import { BookOffInput } from '../dispatch-crew-settings/dispatch-crew-settings.component';

export const STEERING_WHEEL_ICON = "pi pi-steering-wheel pi-custom";

const DEFAULT_REPORT_QUERY_PARAMS = {
  zone: null,
  period: 'Custom',
  periodType: 'eventEnd',
  jobState: 'open;closed;archived',
  eventStatuses: 'required;pending;booked;confirmed;completed',
  jobHasCompletedEstimate: 'false',
  groupBy: 'area',
  sort: 'discountedSubTotal DESC',
  dataColumns: 'area;subTotal;discountedSubTotal;taxTotal;total',
  aggregationColumns: 'subTotalSum;discountedSubTotalSum;totalSum;count;subTotalAvg;discountedSubTotalAvg;totalAvg',
  customPeriod: null,
};
const CAPABILITIES_OPTIONS: DispatchFilterOption<string>[] = [
  {
    label: AdditionalEventAttendeeRoles.All,
    icon: PrimeIcons.USERS
  },
  {
    label: 'CrewLead',
    value: 'CrewLead',
    icon: PrimeIcons.STAR_FILL
  },
  {
    label: 'Driver',
    icon: STEERING_WHEEL_ICON
  }
];

const STATUS_OPTIONS: DispatchFilterOption<string>[] = [
  { label: DispatchUserStatusOptions.Available, icon: PrimeIcons.CHECK_CIRCLE },
  { label: DispatchUserStatusOptions.Assigned, icon: PrimeIcons.CALENDAR },
  { label: DispatchUserStatusOptions.Unassigned, icon: PrimeIcons.MINUS_CIRCLE },
  { label: DispatchUserStatusOptions.Unavailable, icon: PrimeIcons.BAN },
  { label: DispatchUserStatusOptions.Active, icon: PrimeIcons.POWER_OFF },
  { label: DispatchUserStatusOptions.Inactive, icon: PrimeIcons.TRASH },
  { label: DispatchUserStatusOptions.All, icon: PrimeIcons.USERS },
];

const SORT_OPTIONS: DispatchFilterOption<string>[] = [
  { label: CrewSortOptions.Availability, icon: PrimeIcons.CHECK_CIRCLE },
  { label: CrewSortOptions.LastName, icon: PrimeIcons.SORT_ALPHA_DOWN },
  { label: CrewSortOptions.FirstName, icon: PrimeIcons.SORT_ALPHA_DOWN },
  { label: CrewSortOptions.RateHourly, icon: PrimeIcons.DOLLAR },
  { label: CrewSortOptions.EffectiveRate, icon: PrimeIcons.CHART_BAR },
];

@Component({
  selector: 'app-dispatch-crew',
  standalone: true,
  imports: [
    FreyaCommonModule,
    SelectButtonModule,
    IconFieldModule,
    InputIconModule,
    ReactiveFormsModule,
    CdkDrag,
    CdkDropList,
    CommonModule,
    ContextMenuModule,
    DispatchChipComponent,
    SharedModule,
  ],
  providers: [DispatchAssetSearchService],
  templateUrl: './dispatch-crew.component.html',
  styleUrl: './dispatch-crew.component.scss',
})
export class DispatchCrewComponent implements OnInit, OnDestroy {

  public potentialCrew$ = this.store.select(selectPotentialCrew);
  public potentialAssets$ = this.store.select(selectPotentialAssets);
  public usersLoaded$ = this.store.select(selectUsersLoaded);
  public assetsLoaded$ = this.store.select(selectAssetsLoaded);
  public todaysRevenue$ = this.store.select(selectTodaysRevenue);
  public canViewLaborCosts$ = this.permissionService.watchPermission('dispatch.viewLaborCosts');
  public canViewRevenuePerHour$ = this.permissionService.watchPermission('dispatch.viewRevenuePerHour');
  public filteredPotentialCrew$: Observable<PotentialCrewMember[] | SearchResult[]>;
  public filteredPotentialAssets$: Observable<PotentialAssetType | SearchResult[]>;
  // private formattedDate$ = this.store.select(scheduleFeature.selectFormattedDate);

  @ViewChild('csvUpload') csvUpload!: ElementRef<HTMLInputElement>;
  @ViewChild('cm') contextMenu: ContextMenu;
  public crewMenuOptions: MenuItem[] = [];
  public crewContextMenuItems: MenuItem[] = [];
  public selectedContextCrew: PotentialCrewMember;

  public readonly capabilitiesOptions = CAPABILITIES_OPTIONS;
  public readonly statusOptions = STATUS_OPTIONS;
  public readonly sortOptions = SORT_OPTIONS;
  public readonly EVENT_ATTENDEE_ROLES = EventAttendeeRoles;

  public filterCrewForm = new FormGroup({
    userSearch: new FormControl<string>(''),
    assetSearch: new FormControl<string>(''),
    capabilities: new FormControl<string>(this.capabilitiesOptions[0].label),
    status: new FormControl<string>(this.statusOptions[0].label),
    sort: new FormControl<string>(this.sortOptions[0].label),
  });

  public activeView: 'crew' | 'trucks' = 'crew';
  public viewOptions = [
    { label: 'Crew', value: 'crew' },
    { label: 'Trucks', value: 'trucks' }
  ];

  public revenueReportQueryParams = DEFAULT_REPORT_QUERY_PARAMS;
  private bookOffLabel = 'Book Off';

  private subs = new SubSink();

  constructor(
    private store: Store,
    private assetSearchService: DispatchAssetSearchService,
    private router: Router,
    private brandingService: BrandingService,
    private tzHelper: TimezoneHelperService,
    private messageService: MessageService,
    private permissionService: PermissionService,
  ) { }

  ngOnInit(): void {
    this.updateMenu(false);

    this.store.dispatch(DispatchActions.initCrew());

    // eslint-disable-next-line @ngrx/no-store-subscription
    this.subs.sink = this.store.select(selectDispatchDate).subscribe(date => {
      this.bookOffLabel = `Book Off (${dayjs(date).format('MM/DD')})`;
    });

    this.watchUserSearch();
    this.watchAssetSearch();
    this.watchCapabilitiesFilter();
    this.watchStatusFilter();
    this.watchSortFilter();
    this.watchFiltersForMenu();
    // this.prepareDynamicReportLink();
  }

  private watchFiltersForMenu() {
    this.subs.sink = this.filterCrewForm.valueChanges.pipe(
      startWith(this.filterCrewForm.value)
    ).subscribe(val => {
      const isAllAll = val.status === DispatchUserStatusOptions.All && val.capabilities === AdditionalEventAttendeeRoles.All;
      this.updateMenu(isAllAll);
    });
  }

  private updateMenu(showAddCrew: boolean) {
    this.crewMenuOptions = [
      ...(showAddCrew ? [{
        label: 'Add Crew',
        icon: 'pi pi-plus',
        command: () => this.addCrew(),
      }] : []),
      {
        label: 'Download Crew CSV',
        icon: 'pi pi-download',
        command: () => this.downloadCrewCsv(),
      },
      {
        label: 'Upload Crew CSV',
        icon: 'pi pi-upload',
        command: () => this.csvUpload.nativeElement.click(),
      },
      {
        label: 'Copy Crew to Clipboard',
        icon: 'pi pi-copy',
        command: () => this.copyCrewToClipboard(),
      },
    ];
  }

  private watchUserSearch() {
    this.filteredPotentialCrew$ = this.store.select(selectPotentialCrew);

    this.subs.sink = this.filterCrewForm.get('userSearch').valueChanges.pipe(
      debounceTime(300),
    ).subscribe((search) => {
      this.store.dispatch(DispatchActions.setUserSearch({ search }));
    });
  }

  private watchAssetSearch() {
    this.filteredPotentialAssets$ = this.assetSearchService.searchAssets(
      this.filterCrewForm.get('assetSearch').valueChanges.pipe(startWith('')),
      this.potentialAssets$,
    );
  }

  private watchCapabilitiesFilter() {
    this.subs.sink = this.filterCrewForm.get('capabilities').valueChanges.subscribe((label) => {
      this.store.dispatch(DispatchActions.setUserRole({ role: label as any }));
    });
  }

  private watchStatusFilter() {
    this.subs.sink = this.filterCrewForm.get('status').valueChanges.subscribe((label) => {
      this.store.dispatch(DispatchActions.setUserStatus({ status: label as any }));
    });
  }

  private watchSortFilter() {
    this.subs.sink = this.filterCrewForm.get('sort').valueChanges.subscribe((label) => {
      this.store.dispatch(DispatchActions.setCrewSort({ sort: label as any }));
    });
  }

  public addAvailableCrew() {
    this.addCrew();
  }

  public addCrew() {
    const searchName = this.filterCrewForm.get('userSearch').value || '';

    // eslint-disable-next-line @ngrx/avoid-dispatching-multiple-actions-sequentially
    this.store.dispatch(DispatchActions.startCreateCrewMember({ searchName }));

    const defaultTime = '07:00-20:00';
    const availability = Array(7).fill(`Y${defaultTime}`);

    // eslint-disable-next-line @ngrx/avoid-dispatching-multiple-actions-sequentially
    this.store.dispatch(DispatchActions.updateDraftCrewMember({
      user: {
        available: true,
        availability,
      }
    }));
  }

  public expandSearch() {
    this.filterCrewForm.patchValue({
      capabilities: AdditionalEventAttendeeRoles.All,
      status: DispatchUserStatusOptions.All,
    });
  }

  // public prepareDynamicReportLink() {
  //   this.subs.sink = combineLatest([this.formattedDate$, this.brandingService.currentZone()]).subscribe(([formattedDate, zone]) => {
  //     this.revenueReportQueryParams = {
  //       ...DEFAULT_REPORT_QUERY_PARAMS,
  //       zone: zone?.id,
  //       customPeriod: `${formattedDate};${formattedDate}`,
  //     }
  //   });
  // }

  editUser(user: PotentialCrewMember) {
    this.store.dispatch(DispatchActions.editCrewSettingsUserSelected({ user: user.user }));
  }

  async downloadCrewCsv() {
    const crew = (await firstValueFrom(this.filteredPotentialCrew$)) as PotentialCrewMember[];
    this.store.dispatch(DispatchActions.downloadCrewListAsCsv({ crew }));
  }

  async copyCrewToClipboard() {
    const crew = (await firstValueFrom(this.filteredPotentialCrew$)) as PotentialCrewMember[];
    const zone = await firstValueFrom(this.brandingService.currentZone());
    const date = await firstValueFrom(this.store.select(selectDispatchDate));
    const timezone = this.tzHelper.getCurrentTimezone();

    const { blob } = generateCrewCsv(crew, zone.name, date, timezone, '\t');
    const text = await blob.text();
    navigator.clipboard.writeText(text);
    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Crew list copied to clipboard' });
  }

  onCsvUpload(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const reader = new FileReader();
      reader.onload = () => {
        const csvData = reader.result as string;
        this.store.dispatch(DispatchActions.uploadCrewListCsv({ csvData }));
          input.value = '';
          if (this.csvUpload && this.csvUpload.nativeElement) {
            this.csvUpload.nativeElement.value = '';
          }
      };
      reader.readAsText(file);
    }
  }

  public dropCrew($event: CdkDragDrop<any, any, AttendeeDragData>) {
    const { previousContainer, container, item } = $event;

    // Return if the item is dropped in the same container
    if (previousContainer === container) return;

    // Return if no attendee
    if (!item.data?.attendee) return;

    const { attendee, event } = item.data;

    // Return if no event (meaning it didn't come from a dispatch card)
    if (!event) return;

    const eventId = event?.event?.id;
    const eventName = event?.customer?.name;
    const { user, role } = attendee;

    const edits: UpdateCrewActionPayload[] = [
      {
        eventId,
        eventName,
        removeAttendees: [{
          user,
          role,
        }],
      }
    ];

    this.filterCrewForm.get('userSearch').setValue('');
    this.store.dispatch(DispatchActions.updateCrew({ edits }));
  }

  public onRightClick(event: MouseEvent, crew: PotentialCrewMember) {
    this.selectedContextCrew = crew;

    // We need to get dispatch date for label ideally, but it's async.
    // However, usually dispatch view is for a specific date.
    // Let's try to get it synchronously if possible or just use generic label and update logic.
    // The user requirement says "maybe we clarify the date in the menu item".
    // I can subscribe to date in ngOnInit and store it.

    this.crewContextMenuItems = [
      {
        label: 'Edit',
        icon: 'pi pi-pencil',
        command: () => this.editUser(crew),
      },
      {
        label: this.bookOffLabel,
        icon: 'pi pi-calendar-times',
        command: () => this.bookOffUser(crew),
      },
      {
        label: 'Ops Manager',
        icon: 'pi pi-user-plus',
        command: () => this.createOpsManagerEvent(crew),
        visible: crew.user.capabilities?.includes('Operations') ?? false,
      },
      {
        label: 'Non-Billable (Admin)',
        icon: 'pi pi-clock',
        command: () => this.createNonBillableEvent(crew, 'admin'),
      },
      {
        label: 'Non-Billable (Training)',
        icon: 'pi pi-book',
        command: () => this.createNonBillableEvent(crew, 'training'),
      },
      {
        label: 'Reserve',
        icon: 'pi pi-flag',
        command: () => this.createReserveEvent(crew),
      },
    ];
    event.preventDefault();
    event.stopPropagation();
    this.contextMenu.show(event);
  }

  private async bookOffUser(crew: PotentialCrewMember) {
    const user = crew.user;
    const timezone = user.timezone || this.tzHelper.getCurrentTimezone();
    const dispatchDate = await firstValueFrom(this.store.select(selectDispatchDate));
    // Default to "All Day" for the dispatch day
    const start = dayjs(dispatchDate).tz(timezone, true).startOf('day').unix();
    const end = dayjs(dispatchDate).tz(timezone, true).startOf('day').add(86340, 'seconds').unix();

    const bookOffInput: BookOffInput = {
      userId: user.id,
      selectedDate: dispatchDate,
      selectedTimeBlock: {
        label: 'All Day',
        start: 0,
        end: 86340
      },
      description: 'Booked off from Dispatch',
      start,
      end,
      customStartTime: null,
      customEndTime: null,
    };

    this.store.dispatch(DispatchActions.crewBookOffCreated({ newBookOff: bookOffInput }));
  }

  private async createNonBillableEvent(crew: PotentialCrewMember, reason: string) {
    const user = crew.user;
    const timezone = user.timezone || this.tzHelper.getCurrentTimezone();
    const dispatchDate = await firstValueFrom(this.store.select(selectDispatchDate));
    const start = dayjs(dispatchDate).tz(timezone, true).startOf('day').add(7, 'hours').unix();
    const end = dayjs(dispatchDate).tz(timezone, true).startOf('day').add(9, 'hours').unix();

    this.store.dispatch(DispatchActions.nonBillableEventCreated({
      input: {
        eventId: uuid(),
        userId: user.id,
        start,
        end,
        description: `${reason} hours`,
        reason: reason as any,
        selectedDate: dispatchDate,
      },
    }));
  }

  private async createReserveEvent(crew: PotentialCrewMember) {
    const user = crew.user;
    const timezone = user.timezone || this.tzHelper.getCurrentTimezone();
    const dispatchDate = await firstValueFrom(this.store.select(selectDispatchDate));
    const start = dayjs(dispatchDate).tz(timezone, true).startOf('day').add(7, 'hours').unix();
    const end = dayjs(dispatchDate).tz(timezone, true).startOf('day').add(9, 'hours').unix();

    this.store.dispatch(DispatchActions.reserveEventCreated({
      input: {
        eventId: uuid(),
        userId: user.id,
        start,
        end,
        selectedDate: dispatchDate,
      },
    }));
  }

  private async createOpsManagerEvent(crew: PotentialCrewMember) {
    const user = crew.user;
    const timezone = user.timezone || this.tzHelper.getCurrentTimezone();
    const dispatchDate = await firstValueFrom(this.store.select(selectDispatchDate));
    const start = dayjs(dispatchDate).tz(timezone, true).startOf('day').add(7, 'hours').unix();
    const end = dayjs(dispatchDate).tz(timezone, true).startOf('day').add(17, 'hours').unix();

    this.store.dispatch(DispatchActions.opsManagerEventCreated({
      input: {
        eventId: uuid(),
        userId: user.id,
        start,
        end,
        selectedDate: dispatchDate,
      },
    }));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
